/**
 * Offline operation queue – wraps mutating API calls so they can be replayed
 * once the network is available again.
 *
 * Usage:
 *   const r = await runOrQueue({
 *     method: "post",
 *     url: "/projects",
 *     data: { ... },
 *     label: "Projekt anlegen",
 *   });
 *   // r.offline === true if queued; r.data === response if executed online
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  addPendingOp,
  deletePendingOp,
  getAllPendingOps,
  countPendingOps,
  addPendingPhoto,
  deletePendingPhoto,
  getAllPendingPhotos,
  countPendingPhotos,
} from "@/lib/idb";

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `op_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const isNetworkError = (err) => {
  // axios sets err.message === "Network Error" when offline / DNS / connection refused
  if (!err) return false;
  if (err.message === "Network Error") return true;
  if (err.code === "ERR_NETWORK") return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return false;
};

/**
 * Try to execute a mutating call. If network error → enqueue.
 * Returns { offline: true, opId } when queued, or { offline: false, data } on success.
 * Throws non-network errors (validation, 4xx, 5xx) so the caller can show them.
 */
export async function runOrQueue({ method, url, data, label }) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const opId = newId();
    await addPendingOp({ id: opId, method, url, data, label, createdAt: Date.now() });
    notifyQueueChanged();
    return { offline: true, opId };
  }
  try {
    const r = await api.request({ method, url, data });
    return { offline: false, data: r.data };
  } catch (err) {
    if (isNetworkError(err)) {
      const opId = newId();
      await addPendingOp({ id: opId, method, url, data, label, createdAt: Date.now() });
      notifyQueueChanged();
      return { offline: true, opId };
    }
    throw err;
  }
}

/**
 * Try to upload a photo OR video (multipart). On network failure, store the blob
 * in IndexedDB and replay later. `mediaType` is "photo" or "video".
 */
export async function runOrQueueMedia({ projectId, file, category, description, label, mediaType = "photo" }) {
  const endpoint = mediaType === "video" ? "videos" : "photos";
  const buildForm = () => {
    const fd = new FormData();
    fd.append("file", file, file.name || (mediaType === "video" ? "clip.mp4" : "photo.jpg"));
    fd.append("category", category || "Sonstiges");
    fd.append("description", description || "");
    return fd;
  };

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const opId = newId();
    await addPendingPhoto({
      id: opId,
      projectId,
      blob: file,
      filename: file.name || (mediaType === "video" ? "clip.mp4" : "photo.jpg"),
      category: category || "Sonstiges",
      description: description || "",
      label: label || `${mediaType === "video" ? "Video" : "Foto"}: ${category || ""}`,
      mediaType,
      createdAt: Date.now(),
    });
    notifyQueueChanged();
    return { offline: true, opId };
  }
  try {
    const r = await api.post(`/projects/${projectId}/${endpoint}`, buildForm(), {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
    return { offline: false, data: r.data };
  } catch (err) {
    if (isNetworkError(err)) {
      const opId = newId();
      await addPendingPhoto({
        id: opId,
        projectId,
        blob: file,
        filename: file.name || (mediaType === "video" ? "clip.mp4" : "photo.jpg"),
        category: category || "Sonstiges",
        description: description || "",
        label: label || `${mediaType === "video" ? "Video" : "Foto"}: ${category || ""}`,
        mediaType,
        createdAt: Date.now(),
      });
      notifyQueueChanged();
      return { offline: true, opId };
    }
    throw err;
  }
}

/**
 * Try to upload a photo (multipart). Convenience wrapper around runOrQueueMedia.
 */
export async function runOrQueuePhoto(args) {
  return runOrQueueMedia({ ...args, mediaType: "photo" });
}

/**
 * Replays all pending operations sequentially. Stops on first network error
 * (so the rest stays queued for the next attempt). Other errors (4xx/5xx) drop
 * the op from the queue so a single bad payload doesn't block the rest.
 */
let isSyncing = false;

export async function processPendingOps() {
  if (isSyncing) return { synced: 0, failed: 0, remaining: await countPendingOps() };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { synced: 0, failed: 0, remaining: await countPendingOps() };
  }
  isSyncing = true;
  let synced = 0;
  let failed = 0;
  try {
    // 1) JSON mutations
    const ops = await getAllPendingOps();
    ops.sort((a, b) => a.createdAt - b.createdAt);
    for (const op of ops) {
      try {
        await api.request({ method: op.method, url: op.url, data: op.data });
        await deletePendingOp(op.id);
        synced += 1;
        notifyQueueChanged();
      } catch (err) {
        if (isNetworkError(err)) {
          // back offline – stop, keep the rest queued
          break;
        }
        // Server-side rejection: drop this op so it doesn't block the queue
        await deletePendingOp(op.id);
        failed += 1;
        notifyQueueChanged();
      }
    }
    // 2) Photo blobs
    const photos = await getAllPendingPhotos();
    photos.sort((a, b) => a.createdAt - b.createdAt);
    for (const p of photos) {
      try {
        const fd = new FormData();
        fd.append("file", p.blob, p.filename);
        fd.append("category", p.category);
        fd.append("description", p.description);
        const endpoint = p.mediaType === "video" ? "videos" : "photos";
        await api.post(`/projects/${p.projectId}/${endpoint}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await deletePendingPhoto(p.id);
        synced += 1;
        notifyQueueChanged();
      } catch (err) {
        if (isNetworkError(err)) break;
        await deletePendingPhoto(p.id);
        failed += 1;
        notifyQueueChanged();
      }
    }
  } finally {
    isSyncing = false;
  }
  const remaining = (await countPendingOps()) + (await countPendingPhotos());
  return { synced, failed, remaining };
}

/* ---------- Pub/Sub for UI ---------- */

const listeners = new Set();
export function subscribeQueue(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyQueueChanged() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

/* ---------- React hooks ---------- */

export function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function usePendingCount() {
  const [count, setCount] = useState(0);
  const refresh = useCallback(async () => {
    try {
      const ops = await countPendingOps();
      const ph = await countPendingPhotos();
      setCount(ops + ph);
    } catch {
      setCount(0);
    }
  }, []);
  useEffect(() => {
    refresh();
    const unsub = subscribeQueue(refresh);
    return () => { unsub(); };
  }, [refresh]);
  return { count, refresh };
}
